import { useState, useEffect, useCallback, useRef } from "react";
import { logError } from "../utils/logger";

export function useStateWithSync<T>(
  key: string,
  initialValue: T,
  isEnabled: boolean = true, // TODO: remove this when flag is removed https://hello.jira.atlassian.cloud/browse/ASIMO-2163
): [T, (value: T | ((prevState: T) => void)) => void] {
  // Create a unique channel name for this key
  const channelName = `state-sync-${key}`;
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Helper function to safely parse JSON from localStorage
  const getStoredValue = useCallback((): T => {
    if (!isEnabled) {
      return initialValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return initialValue;
      }
      return JSON.parse(item);
    } catch (error) {
      logError({
        error,
        impact: "useStateWithSync: Failed to parse localStorage value",
        extra: { key },
      });
      return initialValue;
    }
  }, [key, initialValue, isEnabled]);

  // Initialize state with value from localStorage or initialValue
  const [state, setState] = useState<T>(getStoredValue);

  const setStoredValue = useCallback(
    (value: T): void => {
      try {
        if (value === undefined) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(value));
        }
      } catch (error) {
        logError({
          error,
          impact: "useStateWithSync: Failed to store value in localStorage",
          extra: { key },
        });
      }
    },
    [key],
  );

  // Custom setter that updates both state and localStorage, and broadcasts the change
  const setStateWithSync = useCallback(
    (value: T | ((prevState: T) => void)): void => {
      setState((prevState) => {
        const newValue = typeof value === "function" ? (value as (prevState: T) => T)(prevState) : value;

        if (!isEnabled) {
          return newValue;
        }

        // Store in localStorage
        setStoredValue(newValue);

        // Broadcast the change to other tabs
        if (channelRef.current) {
          try {
            channelRef.current.postMessage({
              key,
              value: newValue,
              timestamp: Date.now(),
            });
          } catch (error) {
            logError({
              error,
              impact: "useStateWithSync: Failed to broadcast message",
              extra: { key },
            });
          }
        }

        return newValue;
      });
    },
    [key, setStoredValue, isEnabled],
  );

  // Set up BroadcastChannel for cross-tab synchronization
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    // Create the BroadcastChannel
    try {
      channelRef.current = new BroadcastChannel(channelName);
    } catch (error) {
      logError({
        error,
        impact: "useStateWithSync: Failed to create BroadcastChannel",
        extra: { channelName },
      });
      return;
    }

    // Listen for messages from other tabs
    const handleMessage = (event: MessageEvent): void => {
      const { key: messageKey, value } = event.data;

      // Only update if the message is for our key
      if (messageKey === key) {
        setState(value);
      }
    };

    // Add the message listener
    channelRef.current.addEventListener("message", handleMessage);

    // Cleanup function
    return (): void => {
      if (channelRef.current) {
        channelRef.current.removeEventListener("message", handleMessage);
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [key, channelName, isEnabled]);

  return [state, setStateWithSync];
}
